// The locked grid of all ten lenses. Intelligence Tier shows as Applied;
// the three lenses Claude selected as most diagnostic light up sequentially
// with their `whatItReveals` hooks; the remaining seven sit greyed out.
//
// Calling /api/select-models is the funnel-completion event - it also fires
// the lead-notification email server-side (see functions/api/select-models.ts).
// On success we lift the selected lenses up to the parent so MeetingGate can
// name them in the briefing copy.

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2, AlertCircle } from 'lucide-react';
import { elevenModels, APPLIED_MODEL_ID } from '../data/elevenModels';
import {
  postSelectModels,
  type FitResult,
  type SelectedLens,
} from '../lib/api';
import { track } from '../lib/mixpanel';

interface Props {
  orgChart: string;
  diagnosis: string;
  jobTitle?: string;
  fitResult?: FitResult;
  onSelected: (selected: SelectedLens[]) => void;
}

export default function ElevenModelGrid({
  orgChart,
  diagnosis,
  jobTitle,
  fitResult,
  onSelected,
}: Props) {
  const [selected, setSelected] = useState<SelectedLens[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    postSelectModels({ orgChart, diagnosis, jobTitle, fitResult })
      .then((res) => {
        setSelected(res.selected);
        track('models_revealed', { // event 7
          lens_names: res.selected.map((l) => l.name),
          lens_ids: res.selected.map((l) => l.id),
        });
        onSelected(res.selected);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not select lenses.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedById = new Map<number, SelectedLens>();
  if (selected) for (const s of selected) selectedById.set(s.id, s);

  return (
    <section className="bg-paper-deep border-b border-border py-24">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="text-[12px] font-semibold tracking-mega uppercase text-green-700 mb-3">
          The framework
        </div>
        <h2 className="font-display text-[clamp(24px,3vw,32px)] font-semibold tracking-[-0.02em] leading-[1.2] text-ink max-w-[720px] mb-3">
          One lens applied. Nine others sit behind the brief.
        </h2>
        <p className="text-[15px] text-ink-mute leading-[1.7] max-w-[640px] mb-12">
          {selected ? (
            <>Three of them surface specifically from what you described. The other six are part of the framework but not the most diagnostic for your organisation right now.</>
          ) : (
            <>Selecting the three most diagnostic for your organisation now…</>
          )}
        </p>

        <div className="flex flex-wrap gap-6 mb-6 text-[11px] tracking-[0.04em] uppercase text-stone-500 font-medium">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-[3px] bg-green-700" />
            Applied to you
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-[3px] bg-sky-deep" />
            Identified for you
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-[3px] bg-stone-300" />
            Part of the framework
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2 text-[13px] text-clay">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {elevenModels.map((model, idx) => {
            const isApplied = model.id === APPLIED_MODEL_ID;
            const sel = selectedById.get(model.id);
            const isSelected = !!sel;
            const isOther = !isApplied && !isSelected;

            // Stagger the reveal of selected tiles after the API resolves so
            // the user notices them lighting up one at a time.
            const selectedOrder = selected
              ? selected.findIndex((s) => s.id === model.id)
              : -1;
            const revealDelay = isSelected ? 0.2 + selectedOrder * 0.18 : 0;

            const tileClasses = [
              'rounded-lg p-6 border min-h-[152px] flex flex-col transition-colors',
              isApplied && 'bg-green-700 border-green-700',
              isSelected && 'bg-white border-[rgba(91,146,184,0.4)]',
              isOther && 'bg-transparent border-dashed border-border-strong min-h-[96px]',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <motion.article
                key={model.id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.025 }}
                className={tileClasses}
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span
                    className={`font-display text-[12px] font-bold tracking-[0.1em] tabular-nums ${isApplied ? 'text-green-200' : 'text-stone-500'
                      }`}
                  >
                    0{model.id}
                  </span>
                  {isApplied && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] px-2.5 py-[3px] rounded-full bg-[rgba(245,241,233,0.18)] text-paper">
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      Applied
                    </span>
                  )}
                  {isSelected && (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: revealDelay }}
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] px-2.5 py-[3px] rounded-full bg-sky-tint text-sky-deep"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      Identified
                    </motion.span>
                  )}
                </div>

                <h3
                  className={`font-display text-[18px] font-semibold tracking-[-0.01em] leading-[1.25] mb-3 ${isApplied ? 'text-paper' : isSelected ? 'text-sky-deep' : 'text-stone-500 font-medium'
                    }`}
                >
                  {model.name}
                </h3>

                <AnimatePresence>
                  {isSelected && sel && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ delay: revealDelay + 0.1, duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="text-[12.5px] leading-[1.6] text-ink-mute">
                        {sel.rationale}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {isApplied && (
                  <p className="text-[12.5px] leading-[1.6] text-green-100">
                    The lens you've just used. Departments organised by the nature of intelligence, not by traditional function.
                  </p>
                )}

                {!selected && !error && idx === 0 && (
                  <div className="mt-2 text-[11px] text-stone-500 flex items-center gap-1.5">
                    <Loader2 size={11} className="animate-spin" />
                    Reading…
                  </div>
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
